import { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Paper,
	Button,
	CircularProgress,
	Chip,
	Grid2,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getImageUrl } from "../../utils/imageUrl";

const STORE_ITEMS_API = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-store-items`;

const Store = () => {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const navigate = useNavigate();

	useEffect(() => {
		async function fetchItems() {
			setLoading(true);
			setError("");
			try {
				const res = await axios.get(STORE_ITEMS_API, {
					headers: {
						Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
					},
				});
				const data = res.data;
				if (res.status === 200 && data.items) {
					setItems(data.items);
				} else {
					setError(data.error || "Failed to load store items.");
				}
			} catch (err) {
				setError("Failed to load store items.");
			}
			setLoading(false);
		}
		fetchItems();
	}, []);

	// Hide in-stock plant items and only show items tied to a season.
	const visibleItems = items.filter((item) => !!item.season_id);

	// Group visible items by season
	const itemsBySeason = visibleItems.reduce((acc, item) => {
		const seasonName =
			item.seasons && item.seasons.name ? item.seasons.name : "Other";
		if (!acc[seasonName]) acc[seasonName] = [];
		acc[seasonName].push(item);
		return acc;
	}, {});

	if (loading)
		return (
			<Box sx={{ textAlign: "center", mt: 4 }}>
				<CircularProgress />
			</Box>
		);
	if (error)
		return (
			<Box sx={{ textAlign: "center", mt: 4 }}>
				<Typography color="error">{error}</Typography>
			</Box>
		);

	return (
		<Box sx={{ mx: "auto", mt: 4, p: 2 }}>
			<Typography variant="h4" mb={3}>
				Gosell Gardens Online Store
			</Typography>
			<Typography mb={3}>
				Browse our seasonal offerings. In-stock plant sales are currently
				disabled online. Click an item to view details and make a purchase. At
				this time all items are pickup only.
			</Typography>
			{Object.keys(itemsBySeason).length === 0 ? (
				<Typography>No store items available.</Typography>
			) : (
				Object.entries(itemsBySeason).map(([season, seasonItems]) => (
					<Box
						key={season}
						sx={{
							mb: 4,
							width: "100%",
							// allow for responsive design
						}}
					>
						<Typography variant="h5" mb={2}>
							{season}
						</Typography>
						<Grid2
							container
							spacing={2}
							width={"100%"}
							sx={{
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							{seasonItems.map((item) => (
								<Grid2 item xs={12} lg={6} key={item.id} maxWidth={600}>
									<Paper
										key={item.id}
										sx={{
											p: 2,
											mb: 2,
										}}
									>
										<Box
											display="flex"
											justifyContent="space-between"
											alignItems="center"
										>
											<Typography variant="h6">{item.title}</Typography>
											<Chip
												label={
													item.type === "preorder"
														? "Preorder"
														: item.type === "in_stock"
															? "In Stock"
															: "Bundle"
												}
												color={item.type === "preorder" ? "warning" : "default"}
											/>
										</Box>
										<Box
											display="flex"
											justifyContent="space-between"
											alignItems="center"
											sx={{ width: "100%" }}
										>
											<Box sx={{ flex: 1, p: 1 }}>
												<Typography variant="body1" mb={1}>
													{item.description}
												</Typography>
												<Typography variant="subtitle1" mb={1}>
													${(item.price_cents / 100).toFixed(2)}
												</Typography>
												{item.seasons && item.seasons.name && (
													<Typography variant="body2" color="text.secondary">
														Season: {item.seasons.name}
													</Typography>
												)}
												{item.store_item_plants &&
													item.store_item_plants.length > 0 && (
														<Box mt={1}>
															<Typography variant="body2" fontWeight="bold">
																Plants Included:
															</Typography>
															<ul>
																{item.store_item_plants.map((sip) => (
																	<li key={sip.plant_id}>
																		{sip.plants?.plant_name || "Unnamed Plant"}{" "}
																		{sip.quantity_estimate
																			? `(${sip.quantity_estimate})`
																			: ""}
																	</li>
																))}
															</ul>
														</Box>
													)}
												<Button
													variant="contained"
													sx={{ mt: 2 }}
													onClick={() => navigate(`/store/${item.id}`)}
												>
													View Details
												</Button>
											</Box>
											<Box
												sx={{
													minWidth: 120,
													maxWidth: 180,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}
											>
												{item.photo_url && (
													<Box
														sx={{
															width: "100%",
															height: "100%",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															p: 1,
														}}
													>
														<a
															href={getImageUrl(item.photo_url)}
															target="_blank"
															rel="noopener noreferrer"
														>
															<img
																src={getImageUrl(item.photo_url)}
																alt={item.title}
																loading="lazy"
																style={{
																	maxWidth: "100%",
																	maxHeight: 180,
																	borderRadius: 8,
																	cursor: "pointer",
																}}
															/>
														</a>
													</Box>
												)}
											</Box>
										</Box>
									</Paper>
								</Grid2>
							))}
						</Grid2>
					</Box>
				))
			)}
		</Box>
	);
};

export default Store;
